import _ from 'underscore';

import { SiteUrls } from '@tableau/shared-urls';

import { ActionHelpers } from '~commonInfra/contentActions/ActionHelpers';
import { IErrorItem } from '~commonInfra/contentActions/MultipleItemResult';
import { WindowLocationService } from '~commonInfra/services/WindowLocationService';
import { ServiceLocator } from '~src/app/ServiceLocator';
import { SpacesSiteUrls } from '~src/app/spaces/SpacesSiteUrls';
import { DataSources } from '~src/content/resources/Datasources';
import { Workbooks } from '~src/content/resources/Workbooks';
import { ChangeContentOwnerAction } from '~src/content/services/ChangeContentOwnerAction';
import { ChangeContentTagsAction } from '~src/content/services/ChangeContentTagsAction';
import { ChangeDatasourcesTagsAction } from '~src/content/services/ChangeDatasourcesTagsAction';
import { ChangeViewsTagsAction } from '~src/content/services/ChangeViewsTagsAction';
import { ChangeWorkbooksTagsAction } from '~src/content/services/ChangeWorkbooksTagsAction';
import { CreateProjectAction } from '~src/content/services/CreateProjectAction';
import { DeleteContentsAction } from '~src/content/services/DeleteContentsAction';
import { MoveToProjectAction } from '~src/content/services/MoveToProjectAction';
import { SetDisplayTabsAction } from '~src/content/services/SetDisplayTabsAction';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { Projects } from '~src/projects/resources/Projects';
import { ISiteSettingsForServerAdmin } from '~src/settings/types/ISite';
import { ILocLinkifierModel } from '~src/ts/filters/LocLinkifierFilter';
import { IActionContext } from '~src/ts/helpers/ActionContext';
import { Filter } from '~src/ts/helpers/Filter';
import { SpaceInfoItemTypeValue } from '~src/ts/models/ContentType';
import { IContentInfo } from '~src/ts/models/IContentInfo';
import { IDataSourceBase, IDataSourceDetailInfo, IDataSourceInfo } from '~src/ts/models/IDataSourceInfo';
import { IProjectBase, IProjectDetailInfo, IProjectInfo } from '~src/ts/models/IProjectInfo';
import { ISliceable } from '~src/ts/models/ISliceable';
import { IViewBase, IViewDetailInfo } from '~src/ts/models/IViewInfo';
import { IWorkbookBase, IWorkbookDetailInfo, IWorkbookInfo } from '~src/ts/models/IWorkbookInfo';
import {
  IDownloadItemInfo,
  IEditableItemInfo,
  INamedItemInfo,
  IOwnedItemInfo,
  ISpaceTypeInfo,
  ITabbedItemInfo,
  ITaggedItemInfo
} from '~src/ts/models/resourceInfoParts';
import { Set } from '~src/ts/models/Set';
import { IUserName } from '~src/ts/ServerApi/types/IUser';
import { ChangeDatasourceRemoteQueryAgentAction } from '~src/ts/services/ChangeDatasourceRemoteQueryAgentAction';
import { EditSiteAction } from '~src/ts/services/EditSiteAction';
import { RenameAction } from '~src/ts/services/RenameAction';
import { DeleteSitesAction } from '~src/ts/siteManagement/services/DeleteSitesAction';
import { Views } from '~src/views/resources/Views';


export interface IConfirmDialogTypeaheadAttributes<T> {
  model: T;
  displayField: string;
  suggestions: (matchText: string) => ISliceable<any>;
  matchPrefixesOnly?: boolean;
}

export interface IConfirmDialogTypeaheadScope<T> extends ng.IScope {
  description: string;
  typeahead: IConfirmDialogTypeaheadAttributes<T>;
}

export interface IConfirmChangeOwnerScope extends ng.IScope {
  ownerPicker: {
    prompt: string;
    ownerQuery: (query: string) => ISliceable<IUserName>;
    targetOwner: IUserName;
  };
}

export interface IConfirmChangeWorkbookOwnerScope extends ng.IScope {
  ownerPicker: {
    prompt: string;
    promptDescriptionLinkifierModel: () => ILocLinkifierModel;
    ownerQuery: (query: string) => ISliceable<IUserName>;
    targetOwner: IUserName;
  };
}

export interface IDeleteAction {
  (set: Set<any>, actionContext: IActionContext): ng.IPromise<any>;
}

export interface IDownloadAction {
  (downloadItem: IDownloadItemInfo): any;
}

export interface IChangeOwnerAction {
  (set: Set<INamedItemInfo & IOwnedItemInfo>, actionContext: IActionContext): ng.IPromise<any>;
}

export interface IUpdateSiteAvailabilityAction {
  (set: Set<ISiteSettingsForServerAdmin>, actionContext: IActionContext): ng.IPromise<any>;
}

export interface IEditSiteAction {
  (site: ISiteSettingsForServerAdmin, actionContext: IActionContext): ng.IPromise<any>;
}

export interface IEditWorkbookAction {
  (item: IEditableItemInfo): any;
}

export interface IChangeTagsAction {
  (set: Set<ITaggedItemInfo>, actionContext: IActionContext): ng.IPromise<any>;
}

export interface ISetDisplayTabsAction {
  (set: Set<ITabbedItemInfo>, actionContext: IActionContext): ng.IPromise<any>;
}

const getEditSiteAction = () => ServiceLocator.get<EditSiteAction>('EditSiteAction');
const getChangeDatasourceRemoteQueryAgentAction = () => ServiceLocator.get<ChangeDatasourceRemoteQueryAgentAction>('ChangeDatasourceRemoteQueryAgentAction');

class ContentActions {

  // Delete actions

  public deleteProject(projectDetail: IProjectDetailInfo): ng.IPromise<IResult> {
    return Projects.all()
      .withFilters([Filter.fieldEq('id', projectDetail.id)])
      .getFirst()
      .then((projectInfo: IProjectInfo) => {
        return this.deleteProjects(this.singleton(projectInfo));
      });
  }

  public deleteProjects(projects: Set<IProjectInfo>): ng.IPromise<IResult> {
    return DeleteContentsAction.confirmDeleteContents(projects.toArray());
  }

  public deleteWorkbook(workbookDetail: IWorkbookDetailInfo): ng.IPromise<IResult> {
    return Workbooks.all()
      .withFilters([Filter.fieldEq('id', workbookDetail.id)])
      .getFirst()
      .then((workbookInfo: IWorkbookInfo) => {
        return this.deleteWorkbooks(this.singleton(workbookInfo));
      });
  }

  public deleteWorkbooks(workbooks: Set<IWorkbookInfo>): ng.IPromise<IResult> {
    return DeleteContentsAction.confirmDeleteContents(workbooks.toArray());
  }

  // Move to Project

  public moveWorkbooksToProject(workbooks: Set<IWorkbookBase>): Promise<IResult> {
    return MoveToProjectAction.moveWorkbooks(workbooks);
  }

  public moveDatasourcesToProject(datasources: Set<IDataSourceBase>): Promise<IResult> {
    return MoveToProjectAction.moveDatasources(datasources);
  }

  // Create project
  // Returns the new project id
  public createProject(parentId?: string): Promise<IResult> {
    return CreateProjectAction.showCreateProjectDialog(parentId);
  }

  // Helpers

  private singleton<T extends INamedItemInfo>(item: T) {
    return Set.singleton(item, (item: T) => item.id);
  }

  // Rename

  public renameProjectPopup(project: INamedItemInfo): ng.IPromise<IResult> {
    return RenameAction.renameProject(project);
  }

  public renameWorkbookPopup(workbook: INamedItemInfo): ng.IPromise<IResult> {
    return RenameAction.renameWorkbook(workbook);
  }

  public renameViewPopup(view: INamedItemInfo): ng.IPromise<IResult> {
    return RenameAction.renameView(view);
  }

  public renameDatasourcePopup(datasource: INamedItemInfo): ng.IPromise<IResult> {
    return RenameAction.renameDatasource(datasource);
  }

  // Download

  public changeOwnerOnDatasources(datasources: Set<IDataSourceBase>): Promise<IResult> {
    return ChangeContentOwnerAction.confirmChangeOwner(datasources.toArray());
  }

  public changeOwnerOnProjects(projects: Set<IProjectBase>): Promise<IResult> {
    return ChangeContentOwnerAction.confirmChangeOwner(projects.toArray());
  }

  public changeOwnerOnWorkbooks(workbooks: Set<IWorkbookBase>): Promise<IResult> {
    return ChangeContentOwnerAction.confirmChangeOwner(workbooks.toArray());
  }

  // Edit Site Settings

  public editSite(site: ISiteSettingsForServerAdmin, actionContext: IActionContext): ng.IPromise<void> {
    return getEditSiteAction().execute(site, actionContext);
  }

  // Delete Sites

  public deleteSites(sites: Set<ISiteSettingsForServerAdmin>): ng.IPromise<any> {
    return DeleteSitesAction.execute(sites);
  }

  // Authoring

  private authoring(authoringUrl: string) {
    WindowLocationService.open(authoringUrl);
  }

  private generateUniqueAuxiliaryId() {
    const base36Digit = () => {
      const s = '0123456789abcdefghijklmnopqrstuvwxyz';
      return s[Math.floor(Math.random() * s.length)];
    };

    const b36 = (len: number) => {
      let s = '';
      for (let i = 0; i < len; ++i) {
        s += base36Digit();
      }
      return s;
    };

    // To generate the auxiliary ID, use a base-32 encoding of the client time-stamp plus a guid-like base-36 pseudo-unique identifier.
    // We use half the number of digits compared to a standard GUID since base-36 has over twice the number of encoding bits.
    // A collision of identifiers generated in this way is virtually impossible.
    return (new Date()).getTime().toString(32).toLowerCase() + '$' + b36(4) + '-' + b36(2) + '-' + b36(2) + '-' + b36(2) + '-' + b36(6);
  }

  public editWorkbook(workbook: IWorkbookDetailInfo) {
    this.authoring(workbook.editUrl);
  }

  public editView(view: IViewDetailInfo) {
    this.authoring(view.editUrl);
  }

  public newFlow(siteUrlName: string, spaceItemInfo: ISpaceTypeInfo, projectId?: string) {
    this.authoring(this.generateNewFlowUrl(siteUrlName, spaceItemInfo, projectId));
  }

  public newPublishedConnection(projectLuid?: string) {
    this.authoring(this.publishedConnectionEditorUrlForNewPublishedConnection(projectLuid));
  }

  public editPublishedConnection(publishedConnectionId: string) {
    this.authoring(this.publishedConnectionEditorUrlForExistingPublishedConnection(publishedConnectionId));
  }

  private publishedConnectionEditorUrlForNewPublishedConnection(projectLuid?: string) {
    if (projectLuid) {
      return `${this.publishedConnectionEditorPrefix}?project=${projectLuid}`;
    } else {
      return this.publishedConnectionEditorPrefix;
    }
  }

  private publishedConnectionEditorUrlForExistingPublishedConnection(publishedConnectionId: string) {
    return `${this.publishedConnectionEditorPrefix}?published-connection=${publishedConnectionId}`;
  }

  private readonly publishedConnectionEditorPrefix = '/published-connection-editor/';

  public editFlow(siteUrlName: string, flowLuid: string) {
    this.authoring(this.generateEditFlowUrl(siteUrlName, flowLuid));
  }

  public editUnpublishedFlowDraft(urls: SpacesSiteUrls, draftLuid: string) {
    this.authoring(urls.flowEditorDraftUrl(draftLuid));
  }

  public generateNewFlowUrl(siteUrlName: string, spaceItemInfo: ISpaceTypeInfo, projectId?: string) {
    const urlSearchParams = new URLSearchParams();
    if (spaceItemInfo.contentType === SpaceInfoItemTypeValue.datasource) {
      urlSearchParams.set('datasource', (spaceItemInfo as IDataSourceDetailInfo).urlName);
    }
    if (projectId) {
      urlSearchParams.set('projectId', projectId);
    }
    const urlSearchTerm: string = urlSearchParams.toString();
    const siteUrl = new SiteUrls({ siteUrlName });
    const flowLuid = 'new';

    return `${siteUrl.flowEditorFlowPath({ flowLuid: 'new' })}${urlSearchTerm ? `?${urlSearchTerm}` : ''}`;
  }

  public generateEditFlowUrl(siteUrlName: string, flowLuid: string) {
    const siteUrl = new SiteUrls({ siteUrlName });
    return siteUrl.flowEditorFlowPath({ flowLuid: flowLuid });
  }

  public newEmptyWorkbook(siteName: string, projectName?: string) {
    this.authoring(this.generateNewWorkbookUrl('newWorkbook', siteName, projectName));
  }

  public generateNewWorkbookFromDatasourceUrl(datasource: IDataSourceDetailInfo, siteName: string) {
    return `${this.generateNewWorkbookUrl('authoringNewWorkbook', siteName)}/${datasource.urlName}`;
  }

  public generateTempWorkbookFromDatasourceUrlForEdit(datasource: IDataSourceDetailInfo, siteName: string) {
    return `${this.generateNewWorkbookUrl('editDatasource', siteName)}/${datasource.urlName}`;
  }

  public generateTempWorkbookForCreateDatasource(siteName: string) {
    this.authoring(this.generateNewWorkbookUrl('createPublishedDatasource', siteName));
  }

  public generateNewWorkbookUrl(newWorkbookUrlName: string, siteName: string, projectName?: string) {
    return (siteName === '' ? '' : '/t/' + siteName) +
      '/' + newWorkbookUrlName + '/' +
      (!projectName || projectName === '' ? '' : 'p/' + projectName + '/') +
      this.generateUniqueAuxiliaryId();
  }

  public newMetric(datasource: IDataSourceDetailInfo, siteName: string) {
    WindowLocationService.open(this.generateNewMetricForDatasourceUrl(datasource, siteName));
  }

  public generateNewMetricForDatasourceUrl(datasource: IDataSourceDetailInfo, siteName: string) {
    let site = siteName === '' ? '' : '/t/' + siteName;
    return `${site}/metrics/new/${this.generateUniqueAuxiliaryId()}?:ds=${datasource.urlName}&:dsId=${datasource.id}`;
  }

  // Change Tags
  // Change tags section to be removed with removal of reactDetailsDialogs FF
  // replaced with changeTagsForContent in ChangeTagsActionHelper
  public setTagsOnViews(views: Set<IViewBase>): Promise<IResult> {
    return ChangeViewsTagsAction.execute(views);
  }

  public setTagsOnView(view: IViewBase, newTagsList: string[]): Promise<IResult> {
    const tagsToAdd = _.difference(newTagsList, view.tags);
    const tagsToRemove = _.difference(view.tags, newTagsList);

    return Views.changeTags([view.id], tagsToAdd, tagsToRemove)
      .then(result => ActionHelpers.displayStandardToastsForResult(
        Set.fromIdItemArray([view]),
        result,
        ChangeViewsTagsAction.notificationTranslationFns,
        (errorItem: IErrorItem<IViewBase>) => ChangeViewsTagsAction.changeTagsErrorDetail(errorItem)
      ));
  }

  public setTagsOnWorkbook(workbook: IWorkbookBase, newTagsList: string[]): Promise<IResult> {
    const tagsToAdd = _.difference(newTagsList, workbook.tags);
    const tagsToRemove = _.difference(workbook.tags, newTagsList);

    return Workbooks.changeTags([workbook.id], tagsToAdd, tagsToRemove)
      .then(result => ActionHelpers.displayStandardToastsForResult(
        Set.fromIdItemArray([workbook]),
        result,
        ChangeWorkbooksTagsAction.notificationTranslationFns,
        (errorItem: IErrorItem<IWorkbookBase>) => ChangeWorkbooksTagsAction.changeTagsErrorDetail(errorItem)
      ));
  }

  public setTagsOnWorkbooks(workbooks: Set<IWorkbookBase>): Promise<IResult> {
    return ChangeWorkbooksTagsAction.execute(workbooks);
  }

  public setTagsOnDatasource(datasource: IDataSourceInfo, newTagsList: string[]): Promise<IResult> {
    const tagsToAdd = _.difference(newTagsList, datasource.tags);
    const tagsToRemove = _.difference(datasource.tags, newTagsList);

    return DataSources.changeTags([datasource.id], tagsToAdd, tagsToRemove)
      .then(result => ActionHelpers.displayStandardToastsForResult(
        Set.fromIdItemArray([datasource]),
        result,
        ChangeDatasourcesTagsAction.notificationTranslationFns,
        (errorItem: IErrorItem<IDataSourceBase>) => ChangeDatasourcesTagsAction.changeTagsErrorDetail(errorItem)
      ));
  }

  public setTagsOnDatasources(datasources: Set<IDataSourceBase>): Promise<IResult> {
    return ChangeDatasourcesTagsAction.showChangeTagsDialog(datasources);
  }

  public setTagsOnMixedContent(content: IContentInfo[]): Promise<IResult> {
    return ChangeContentTagsAction.showChangeTagsForMixedContentDialog(content);
  }

  // Set Display Tabs

  public setDisplayTabsOnWorkbook(workbooks: Set<IWorkbookBase>): Promise<IResult> {
    return SetDisplayTabsAction.confirmSetDisplayTabs(workbooks);
  }

  public changeRemoteQueryAgentOnDatasource(datasource: IDataSourceDetailInfo): Promise<IResult> {
    return getChangeDatasourceRemoteQueryAgentAction().execute(datasource);
  }

  public openWhoHasSeenThisView(viewId: string) {
    const whoHasSeenThisViewLink = '/vizql/showadminview/views/WhoHasSeen?views_id=' + viewId;
    WindowLocationService.open(whoHasSeenThisViewLink);
  }
}

export const ContentActionsService = new ContentActions();
// export only the type of the class, since we don't want modules to be able to instantiate it
export type { ContentActions };
